// Shared programme-level recommendation context.
//
// Used by BOTH the Recommendations header (OverviewTab) and the Implementation
// tab Program Scope card so the two surfaces always display identical,
// override-aware values. This is display-only logic — engines are never re-run.

// Canonicalize any deployment label into one of the 4 programme buckets.
export function normalizeDeployment(d) {
  const x = String(d || "").toLowerCase();
  if (!x || x === "\u2014") return null;
  if (x.includes("decentral")) return "Decentralized EWM";
  if (x.includes("hybrid")) return "Hybrid Deployment";
  if (x.includes("lean") || x.includes("n/a")) return "N/A";
  if (x.includes("embedded")) return "Embedded EWM";
  return d;
}

// Programme-level Deployment Architecture derived from the EFFECTIVE per-site
// recommendations: pure lean solution → N/A; Hybrid Landscape →
// Hybrid Deployment; all sites same → that value; mixed → Hybrid Deployment.
export function deriveProgrammeDeployment(effectiveSolutions, solutionDisplay, perSiteDeployments) {
  const solSet = (effectiveSolutions || []).map((s) => String(s).toLowerCase()).filter(Boolean);
  const allLean = solSet.length > 0
    && solSet.every((s) => s.includes("lgm") || s.includes("stock room"));
  if (allLean) return "N/A";
  if (String(solutionDisplay || "").toLowerCase().includes("hybrid landscape")) {
    return "Hybrid Deployment";
  }
  const canon = Array.from(new Set((perSiteDeployments || []).map(normalizeDeployment).filter(Boolean)));
  if (canon.length === 0) return "\u2014";
  if (canon.length === 1) return canon[0];
  return "Hybrid Deployment";
}

// Numeric site total — reconciled `implementation_inputs.total_warehouses`,
// else sum of Module 3 `op_warehouse_count` (fallback 1 per representative site).
export function siteCountOf(assessment, siteKeys) {
  let total = Number(assessment?.implementation_inputs?.total_warehouses);
  if (!Number.isFinite(total) || total <= 0) {
    const m3 = assessment?.module_data?.module3 || {};
    const keys = (siteKeys && siteKeys.length) ? siteKeys : Object.keys(m3);
    total = keys.reduce((n, k) => {
      const c = Number(m3?.[k]?.op_warehouse_count);
      return n + (Number.isFinite(c) && c > 0 ? c : 1);
    }, 0);
  }
  return total > 0 ? total : 0;
}

// Coverage site count. Mirrors the Implementation tab's CALCULATED total —
// never the client-entered warehouse-count band, never "0".
export function formatLocationCoverage(assessment, siteKeys) {
  const total = siteCountOf(assessment, siteKeys);
  if (total > 0) return `${total} site${total === 1 ? "" : "s"}`;
  return "Sites not specified";
}

const SUPPLY_CHAIN_ORDER = ["manufacturing", "cdc", "rdc", "satellite"];

function orderedSiteKeys(recs) {
  return Object.keys(recs || {}).sort((a, b) => {
    const ia = SUPPLY_CHAIN_ORDER.indexOf(a);
    const ib = SUPPLY_CHAIN_ORDER.indexOf(b);
    const ra = ia === -1 ? 99 : ia;
    const rb = ib === -1 ? 99 : ib;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

// Single source of truth for the programme-level recommendation context shown in
// the Recommendations header and the Implementation Program Scope card.
//
// `effectiveData` is the payload from GET .../recommendations/effective (may be
// null for legacy / non-SmartEvaluate assessments — system values are used then).
export function deriveProgrammeContext(assessment, effectiveData) {
  const a = assessment || {};
  const recs = a.final_recommendation_by_site || {};
  const deploys = a.deployment_recommendation_by_site || {};
  const lsObj = a.current_sap_landscape || {};
  // Mirror EvaluationInsightsTab.readLandscape: prefer the landscape object,
  // fall back to top-level assessment fields, treat ""/null as absent.
  const pickLs = (k) => {
    const v = lsObj[k] ?? a[k];
    return (v == null || v === "") ? null : v;
  };
  const roiSummary = (a.roi_output || {}).overall_roi_summary || {};
  const siteKeys = orderedSiteKeys(recs);

  const effSolBySite = effectiveData?.effective_solution_by_site || {};
  const effDepBySite = effectiveData?.effective_deployment_by_site || {};
  const siteSolution = (s) => effSolBySite[s] || recs[s] || "\u2014";
  const siteDeployment = (s) => effDepBySite[s] || deploys[s] || "\u2014";

  // Solution Strategy — effective per-site solutions (override-aware). Derived
  // from the effective per-site map so a saved Advisory Override flows through
  // to the Implementation and Executive Summary tabs. The ROI display label is
  // only a last-resort fallback when no per-site solution is available.
  const solutions = Array.from(new Set(siteKeys.map(siteSolution).filter((v) => v && v !== "\u2014")));
  const isHybrid = solutions.length > 1;
  const solutionDisplay = solutions.length === 1
    ? solutions[0]
    : solutions.length > 1
      ? `Hybrid (${solutions.map((s) => String(s).replace("SAP ", "")).join(" + ")})`
      : (roiSummary.recommendation_display || "\u2014");

  const deployment = deriveProgrammeDeployment(
    siteKeys.map(siteSolution), solutionDisplay, siteKeys.map(siteDeployment),
  );

  // Hosting Model — single source of truth = the same recommendation object
  // Evaluation Insights renders from. Programme override wins; else the engine
  // landscape hosting; else the Hosting Advisory (customer preferred when set,
  // otherwise architect best-fit). `||` so empty-string labels fall through.
  // Hosting RECOMMENDATION. For Greenfield/Brownfield (Option C) the system
  // recommendation is the engine's architect best-fit; effective precedence is
  // saved consultant override → architect best-fit → Not Decided. The customer
  // preference (ls.hosting_model / preferred_hosting_model) is NEVER used as the
  // recommendation — it stays in Evaluation Insights. Cloud Migration keeps its
  // prior behaviour (untouched).
  const ha = a.hosting_advisory || {};
  const overrideHosting = effectiveData?.summary?.global_override?.hosting_model || null;
  const isCloudMigration = String(a.scenario_type || "").toLowerCase() === "cloud_migration";
  let hostingModel;
  if (isCloudMigration) {
    const cmAdvisory = (
      (ha.customer_preferred_hosting && ha.customer_preferred_hosting !== "Not specified"
        ? ha.customer_preferred_hosting
        : ha.architect_best_fit_hosting) || null
    );
    hostingModel = (effectiveData?.summary?.overall_hosting || pickLs("hosting_model") || cmAdvisory) || "Not Specified";
  } else {
    hostingModel = (overrideHosting || ha.architect_best_fit_hosting) || "Not Decided";
  }
  // Transformation Pattern — derived landscape metadata (display-only).
  const transformationPattern = pickLs("transformation_approach") || "Best-Fit Evaluation";
  const siteCount = siteCountOf(a, siteKeys);
  const coverageLocations = formatLocationCoverage(a, siteKeys);
  const isGrouped = Array.isArray(a.warehouse_groups) && a.warehouse_groups.length > 0;
  const unitWord = isGrouped ? "Warehouse Group" : "Warehouse Type";
  const coverageSummary = `${siteKeys.length} ${unitWord}${siteKeys.length === 1 ? "" : "s"} | ${siteCount} Site${siteCount === 1 ? "" : "s"}`;

  return {
    siteKeys,
    typesCount: siteKeys.length,
    siteCount,
    isHybrid,
    solutionDisplay,
    deployment,
    hostingModel,
    transformationPattern,
    coverageLocations,
    coverageSummary,
  };
}
